import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import MobileShell from '@/components/MobileShell';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { clearServiceConfigCache } from '@/hooks/useServiceConfig';
import { pricingStatusClass, pricingStatusLabel } from '@/lib/pricingConfig';

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/admin/services');
      setServices(data.services || []);
    } catch {
      toast.error('Could not load services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleBroadcast = async (serviceKey, currentActive) => {
    setUpdating(serviceKey);
    try {
      await api.patch(`/admin/services/${serviceKey}`, {
        broadcast_active: !currentActive,
      });
      clearServiceConfigCache();
      await load();
      toast.success(
        !currentActive
          ? 'Broadcast activated for consumers'
          : 'Service returned to existing flow',
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setUpdating(null);
    }
  };

  return (
    <MobileShell title="Service Operations">
      <div className="px-5 py-5 space-y-3">
        <p className="text-sm text-slate-600">
          Toggle live broadcast matching per service. When off, consumers use the existing waitlist flow.
        </p>

        {loading ? (
          <div className="text-sm text-slate-500">Loading services…</div>
        ) : (
          services.map((svc) => {
            const isPharmacy = svc.service_key === 'pharmacy';
            const active = !isPharmacy && svc.enabled && svc.operation_mode === 'broadcast';
            const modeLabel = isPharmacy ? 'Dedicated workflow' : (active ? 'Broadcast' : 'Existing');
            return (
              <div key={svc.service_key} className="resqly-card p-4" data-testid={`admin-service-${svc.service_key}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-slate-900">{svc.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5 capitalize">{svc.service_key.replace(/_/g, ' ')}</div>
                    <div className="text-xs mt-1 flex flex-wrap gap-1">
                      <span className={`font-semibold px-2 py-0.5 rounded-full ${
                        active ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {modeLabel}
                      </span>
                      <span
                        data-testid={`pricing-status-${svc.service_key}`}
                        className={`font-semibold px-2 py-0.5 rounded-full ${pricingStatusClass(svc)}`}
                      >
                        {pricingStatusLabel(svc)}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {isPharmacy ? (
                      <span className="text-[10px] font-semibold text-emerald-700 uppercase">Controlled in Pharmacy</span>
                    ) : (
                      <>
                        <span className="text-[10px] font-semibold text-slate-500 uppercase">Activate Broadcast</span>
                        <Switch
                          data-testid={`admin-toggle-${svc.service_key}`}
                          checked={active}
                          disabled={updating === svc.service_key || !svc.has_broadcast_schemas}
                          onCheckedChange={() => toggleBroadcast(svc.service_key, active)}
                        />
                      </>
                    )}
                  </div>
                </div>
                {!isPharmacy && !svc.has_broadcast_schemas && (
                  <p className="text-xs text-amber-600 mt-2">Broadcast schemas not configured for this service.</p>
                )}
                <Link
                  to={isPharmacy ? '/admin/pharmacy' : `/admin/services/${svc.service_key}/pricing`}
                  data-testid={`configure-pricing-${svc.service_key}`}
                  className="mt-3 inline-block text-sm font-semibold text-blue-700"
                >
                  {isPharmacy ? 'Open Pharmacy Control Centre' : 'Configure Pricing'}
                </Link>
              </div>
            );
          })
        )}

        <Link
          to="/admin/provider-dispatch"
          className="resqly-card p-4 block text-center text-sm font-semibold text-blue-700 hover:shadow-md"
          data-testid="admin-provider-dispatch-link"
        >
          Provider Dispatch — Incoming Request Ringtone
        </Link>

        <Link to="/admin/dashboard" className="block text-center text-sm text-blue-700 font-semibold py-4">
          Back to Admin Console
        </Link>
      </div>
    </MobileShell>
  );
}
